import { DatePipe, registerLocaleData } from "@angular/common";
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
} from "@angular/common/http";
import localeDa from "@angular/common/locales/da";
import localeDe from "@angular/common/locales/de";
import localeEn from "@angular/common/locales/en";
import localeFr from "@angular/common/locales/fr";
import localeNb from "@angular/common/locales/nb";
import localeNl from "@angular/common/locales/nl";
import localePl from "@angular/common/locales/pl";
import localeSv from "@angular/common/locales/sv";
import {
	CUSTOM_ELEMENTS_SCHEMA,
	inject,
	LOCALE_ID,
	NgModule,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { BASE_PATH, Configuration } from "@api/face-scan/src";
import { WpaService } from "@data/services/api/wpa.service";
import { WpaBaseService } from "@data/services/interfaces/wpa-base.service";
import { WpaMockService } from "@data/services/mock/wpa-mock.service";
import { BASE_API_URL } from "@data/tokens/base-api-url.token";
import { NavbarComponent } from "@layout/components/navbar/navbar.component";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import {
	TranslateLoader,
	TranslateModule,
	TranslateService,
	TranslationObject,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { CardComponent } from "@shared/components/card/card.component";
import { CarouselComponent } from "@shared/components/carousel/carousel.component";
import { CarouselItemDirective } from "@shared/components/carousel/carousel-item.directive";
import { CollapseComponent } from "@shared/components/collapse/collapse.component";
import { MainSectionButtonComponent } from "@shared/components/main-section-button/main-section-button.component";
import { authenticationTokenInterceptor } from "@shared/interceptors/authentification-token.interceptor";
import { languageInterceptor } from "@shared/interceptors/language.interceptor";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import { Observable } from "rxjs";
import { environment } from "./../environments/environment";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { ControlsModule } from "./controls/controls.module";
import { MenuButtonComponent } from "./controls/menu-button/menu-button.component";
import { SurveySlideComponent } from "./controls/survey-slide/survey-slide.component";
import { ConsentComponent } from "./login/consent/consent.component";
import { CriiptologinComponent } from "./login/criiptologin/criiptologin.component";
import { HistoryloginComponent } from "./login/historylogin/historylogin.component";
import { InvalidComponent } from "./login/invalid/invalid.component";
import { LangComponent } from "./login/lang/lang.component";
import { LoginComponent } from "./login/login/login.component";
import { NologinComponent } from "./login/nologin/nologin.component";
import { UserinfoComponent } from "./login/userinfo/userinfo.component";
import { UserloginComponent } from "./login/userlogin/userlogin.component";
import { ScoreAnswerComponent } from "./modules/health-screening/health-screening-score-details/components/score-answer/score-answer.component";
import { ScoreGroupComponent } from "./modules/health-screening/health-screening-scores/components/score-group.component";
import { HomeComponent } from "./pages/home/home.component";
import { NotificationsComponent } from "./pages/notifications/notifications/notifications.component";
import { NotificationsMessageComponent } from "./pages/notifications/notifications-message/notifications-message.component";
import { ProfileComponent } from "./pages/profile/profile/profile.component";
import { ProfileConsentsComponent } from "./pages/profile/profile-consents/profile-consents.component";
import { StartComponent } from "./pages/start/start.component";
import { RoundPipe } from "./pipes/round.pipe";
import { SafePipe } from "./pipes/safe.pipe";
import { BrochureComponent } from "./shared/components/brochure/brochure.component";
import { loadingInterceptor } from "./shared/interceptors/loading/loading.interceptor";
import { ImagePipe } from "./shared/pipes/image/image.pipe";
import { SinglebasicDirective } from "./singlebasic.directive";
import { BundleComponent } from "./survey/controls/bundle/bundle.component";
import { MultiComponent } from "./survey/controls/multi/multi.component";
import { MultilineComponent } from "./survey/controls/multiline/multiline.component";
import { NumberComponent } from "./survey/controls/number/number.component";
import { NumberdecimalComponent } from "./survey/controls/numberdecimal/numberdecimal.component";
import { NumberdropdownComponent } from "./survey/controls/numberdropdown/numberdropdown.component";
import { ScaleComponent } from "./survey/controls/scale/scale.component";
import { SingleComponent } from "./survey/controls/single/single.component";
import { SingleBasicComponent } from "./survey/controls/single-basic/single-basic.component";
import { SurveyComponent } from "./survey/survey/survey.component";
import { SurveygroupComponent } from "./survey/surveygroup/surveygroup.component";
import { SurveylastComponent } from "./survey/surveylast/surveylast.component";
import { SurveynoreportComponent } from "./survey/surveynoreport/surveynoreport.component";
import { SurveyControlsComponent } from "./survey/surveyquestions/components/survey-controls/survey-controls.component";
import { SurveyQuestionComponent } from "./survey/surveyquestions/components/survey-question/survey-question.component";
import { SurveyQuestionsComponent } from "./survey/surveyquestions/survey-questions.component";
import { TrainingreportvideoComponent } from "./video/trainingreportvideo/trainingreportvideo.component";
import { TrainingvideoComponent } from "./video/trainingvideo/trainingvideo.component";

// AV-031: date/time pipes without an explicit locale fall back to Angular's
// default (en-US) unless the locale data is registered here and LOCALE_ID is
// provided below - covers every language in Languages.json, not just Norwegian.
registerLocaleData(localeDa);
registerLocaleData(localeEn);
registerLocaleData(localeNb);
registerLocaleData(localeFr);
registerLocaleData(localeNl);
registerLocaleData(localeDe);
registerLocaleData(localeSv);
registerLocaleData(localePl);

// AoT requires an exported function for factories
export function HttpLoaderFactory() {
	return new TranslateHttpLoader();
}

export class dynamicTranslateLoader implements TranslateLoader {
	constructor(private http: HttpClient) {}

	public getTranslation(lang: string): Observable<TranslationObject> {
		return this.http.get<TranslationObject>(
			"/assets/i18n/" + environment.WhitelabelName + "/" + lang + ".json",
		);
	}
}

export function dynamicTransLoaderFactory(http: HttpClient) {
	return new dynamicTranslateLoader(http);
}

@NgModule({
	declarations: [AppComponent],
	exports: [TranslateModule],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		FormsModule,
		BrowserModule,
		AppRoutingModule,
		ReactiveFormsModule,
		NgxIntlTelInputModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: dynamicTransLoaderFactory,
				deps: [HttpClient],
			},
		}),
		NgbModule,
		BrowserAnimationsModule,
		ControlsModule,
		SurveyQuestionComponent,
		SurveyControlsComponent,
		BundleComponent,
		SingleBasicComponent,
		SingleComponent,
		MultiComponent,
		MultilineComponent,
		ScaleComponent,
		NumberComponent,
		NumberdecimalComponent,
		SafePipe,
		MenuButtonComponent,
		ScoreGroupComponent,
		ScoreAnswerComponent,
		BrochureComponent,
		MainSectionButtonComponent,
		CardComponent,
		ImagePipe,
		SurveySlideComponent,
		NavbarComponent,
		CarouselComponent,
		CarouselItemDirective,
		CollapseComponent,
		SurveyComponent,
		LoginComponent,
		LangComponent,
		SurveyQuestionsComponent,
		SinglebasicDirective,
		NumberdropdownComponent,
		RoundPipe,
		TrainingvideoComponent,
		TrainingreportvideoComponent,
		UserloginComponent,
		SurveylastComponent,
		SurveygroupComponent,
		SurveynoreportComponent,
		InvalidComponent,
		NologinComponent,
		HistoryloginComponent,
		CriiptologinComponent,
		UserinfoComponent,
		ConsentComponent,
		HomeComponent,
		ProfileComponent,
		NotificationsComponent,
		StartComponent,
		ProfileConsentsComponent,
		NotificationsMessageComponent,
	],
	providers: [
		provideHttpClient(
			withInterceptors([
				authenticationTokenInterceptor,
				languageInterceptor,
				loadingInterceptor,
			]),
			withInterceptorsFromDi(),
		),
		{
			provide: BASE_PATH,
			useValue: environment.FaceScanApiBaseUrl,
		},
		{
			provide: Configuration,
			useFactory: () => {
				return new Configuration({
					basePath: environment.FaceScanApiBaseUrl,
					credentials: {
						ApiToken: () => sessionStorage.getItem("ticket") ?? undefined,
					},
				});
			},
		},
		{
			provide: BASE_API_URL,
			useValue: environment.BookingApiBaseUrl,
		},
		Window,
		DatePipe,
		{
			provide: LOCALE_ID,
			// AV-031: dates are always shown in Norwegian, regardless of the
			// selected UI language - Salubi does not want dates flipping to
			// English day/month names even when the English translation is active.
			useValue: "nb-NO",
		},
		{
			provide: WpaBaseService,
			useClass: environment.name === "mock" ? WpaMockService : WpaService,
		},
	],
})
export class AppModule {
	private translate = inject(TranslateService);

	constructor() {
		// this language will be used as a fallback when a translation isn't found in the current language
		this.translate.setDefaultLang(environment.Language);
	}
}
